'use client'
import React, { useRef, useImperativeHandle } from 'react'
import { Picture, Text, Heading } from '../../components/index.js'
import { cn } from '../../helpers/utils.js'
import { withLayout } from '../../shared/Styles.js'
import { useExposure } from '../../hooks/useExposure.js'
import type { FeatureShowcaseProps, FeatureShowcaseItem } from './types.js'

const componentType = 'image'
const componentName = 'feature_showcase'

const FeatureShowcase = React.forwardRef<HTMLDivElement, FeatureShowcaseProps>(({ data, className }, ref) => {
  const { datalist = [], datalists = [], theme = 'light' } = data || {}

  const boxRef = useRef<HTMLDivElement>(null)

  useExposure(boxRef, {
    componentType,
    componentName,
    componentTitle: 'Feature Showcase',
  })

  useImperativeHandle(ref, () => boxRef.current as HTMLDivElement)

  return (
    <section
      ref={boxRef}
      data-ui-component-id="FeatureShowcase"
      className={cn(
        'feature-showcase scrollbar-hidden text-info-primary l:flex-row l:overflow-hidden l:overflow-x-scroll laptop:flex-col flex gap-[16px]',
        {
          'aiui-dark': theme === 'dark',
        },
        className
      )}
    >
      <div className="feature-showcase__list flex gap-[16px]">
        {datalist?.map((item: FeatureShowcaseItem, index: number) => (
          <div key={index} className="feature-showcase__item">
            <div className="feature-showcase__card relative">
              <Picture
                source={`${item.imageMob?.url}`}
                className="feature-showcase__image feature-showcase__image--mobile rounded-box laptop:hidden w-[296px]"
              />
              <Picture
                source={`${item.image?.url} 1024`}
                className="feature-showcase__image feature-showcase__image--desktop l:hidden"
              />
              <div className="feature-showcase__content laptop:px-[16px] laptop:py-[20px] desktop:px-[32px] desktop:py-[24px] lg-desktop:p-[32px] absolute bottom-0 left-0 px-[16px] py-[12px] text-white">
                <Heading
                  as={'h2'}
                  size={3}
                  html={item?.title}
                  className="feature-showcase__title mt-[16px] md:text-[20px]"
                />

                <Text
                  as={'p'}
                  html={item.desc}
                  size={4}
                  className="feature-showcase__description tablet:text-[14px] laptop:text-[14px] desktop:h-fit desktop:text-[16px] lg-desktop:text-[18px] mt-[4px] line-clamp-2 h-[40px] text-[14px]"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="feature-showcase__list flex gap-[16px]">
        {datalists?.map((item: FeatureShowcaseItem, index: number) => (
          <div key={index} className="feature-showcase__item">
            <div className="feature-showcase__card relative">
              <Picture
                source={`${item.imageMob?.url}`}
                className="feature-showcase__image feature-showcase__image--mobile rounded-box laptop:hidden w-[296px]"
              />
              <Picture
                source={`${item.image?.url} 1024`}
                className="feature-showcase__image feature-showcase__image--desktop l:hidden"
              />
              <div className="feature-showcase__content laptop:px-[16px] laptop:py-[20px] desktop:px-[32px] desktop:py-[24px] lg-desktop:p-[32px] absolute bottom-0 left-0 px-[16px] py-[12px] text-white">
                <Heading
                  as={'h2'}
                  size={3}
                  html={item?.title}
                  className="feature-showcase__title mt-[16px] md:text-[20px]"
                />

                <Text
                  as={'p'}
                  html={item.desc}
                  size={4}
                  className="feature-showcase__description tablet:text-[14px] laptop:text-[14px] desktop:h-fit desktop:text-[16px] lg-desktop:text-[18px] mt-[4px] line-clamp-2 h-[40px] text-[14px]"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
})

FeatureShowcase.displayName = 'FeatureShowcase'

export default withLayout(FeatureShowcase)
